import type {
    FileValidationResult,
    MinMax,
    FileValidationOptions,
} from '@modules/file/util/validate-files';
import { validateFiles } from '@modules/file/util/validate-files';
import {
    DEFAULT_FILE_UPLOAD_ERROR,
    generateValidationMessage,
    INVALID_IMAGE_ORIENTATION_MESSAGE,
    INVALID_MAX_IMAGE_DIMENSIONS_MESSAGE,
    INVALID_MIN_IMAGE_DIMENSIONS_MESSAGE,
} from '@modules/file/config/validation-messages';
import type { ImagesWithPreviewPromise } from '@modules/file/util/create-image-preview';
import { createPreviewImages } from '@modules/file/util/create-image-preview';
import type { ImageWithPreview } from '@modules/page-builder/types';

interface ImageValidationProps {
    files: File[];
    validationOptions: ImageValidationOptions;
}

export type ImageOrientation = 'landscape' | 'portrait';

export interface ImageValidationOptions extends FileValidationOptions {
    imageWidth?: MinMax;
    imageHeight?: MinMax;
    orientations?: ImageOrientation[];
}

export type ImageValidationResult = FileValidationResult & {
    imagesWithPreview?: ImageWithPreview[];
};

export async function validateImages({
    files,
    validationOptions,
}: ImageValidationProps): Promise<ImageValidationResult> {
    let result: ImageValidationResult = validateFiles({
        files,
        validationOptions: validationOptions,
    });

    if (result.valid) {
        const imagesWithPreview: ImagesWithPreviewPromise =
            await createPreviewImages(files);
        if (Array.isArray(imagesWithPreview)) {
            imagesWithPreview.every((image) => {
                result = validateImageDimensions(
                    image,
                    validationOptions.imageWidth,
                    validationOptions.imageHeight,
                    validationOptions.orientations
                );
                return result.valid;
            });

            result.imagesWithPreview = imagesWithPreview;
        } else {
            result = { valid: false, message: DEFAULT_FILE_UPLOAD_ERROR };
        }
    }
    return result;
}

function validateImageDimensions(
    image: ImageWithPreview,
    width?: MinMax,
    height?: MinMax,
    orientations?: ImageOrientation[]
): FileValidationResult {
    if (image.width && image.height && width && height) {
        if (
            width.min &&
            height.min &&
            (image.width < width.min || image.height < height.min)
        ) {
            return {
                valid: false,
                message: generateValidationMessage(
                    INVALID_MIN_IMAGE_DIMENSIONS_MESSAGE,
                    [
                        Math.ceil(width.min).toString(),
                        Math.ceil(height.min).toString(),
                    ]
                ),
            };
        }
        if (
            width.max &&
            height.max &&
            (image.width < width.max || image.height < height.max)
        ) {
            return {
                valid: false,
                message: generateValidationMessage(
                    INVALID_MAX_IMAGE_DIMENSIONS_MESSAGE,
                    [
                        Math.ceil(width.max).toString(),
                        Math.ceil(height.max).toString(),
                    ]
                ),
            };
        }
        if (
            image.orientation &&
            orientations &&
            !orientations.includes(image.orientation)
        ) {
            return {
                valid: false,
                message: generateValidationMessage(
                    INVALID_IMAGE_ORIENTATION_MESSAGE,
                    [orientations.join(' or ')]
                ),
            };
        }
    }

    return { valid: true };
}
